import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import type { User } from 'firebase/auth';
import { firestore } from './config'; // Assuming config.ts exports initialized firestore

export interface UserProfile {
  uid: string;
  email: string | null;
  displayName: string | null;
  role: 'admin' | 'user' | 'pro';
  createdAt: string;
}

const usersCollection = 'users';

/**
 * Fetches a user's profile from Firestore.
 * @param userId The UID of the user.
 * @returns The user profile data or null if not found.
 */
export const getUserProfile = async (
  userId: string
): Promise<UserProfile | null> => {
  try {
    const userDocRef = doc(firestore, usersCollection, userId);
    const docSnap = await getDoc(userDocRef);

    if (docSnap.exists()) {
      // Type assertion might be needed depending on strictness,
      // or use a validation library like Zod.
      return docSnap.data() as UserProfile;
    } else {
      console.log('No such user profile document!');
      return null;
    }
  } catch (error) {
    console.error('Error getting user profile:', error);
    // Consider more robust error handling/logging
    throw error; // Re-throw or handle as needed
  }
};

/**
 * Creates a new user profile document in Firestore.
 * Defaults the role to 'user' if not specified.
 * @param user The Firebase Auth User object.
 * @param role The role to assign ('admin' or 'user'). Defaults to 'user'.
 */
export const createUserProfile = async (
  user: User,
  role: UserProfile['role'] = 'user'
): Promise<void> => {
  const userDocRef = doc(firestore, usersCollection, user.uid);

  const newUserProfile: Omit<UserProfile, 'createdAt'> & {
    createdAt: ReturnType<typeof serverTimestamp>;
  } = {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    role: role,
    createdAt: serverTimestamp(), // Use server timestamp
  };

  try {
    // Use setDoc to create the document with the specific UID
    await setDoc(userDocRef, newUserProfile);
    console.log('User profile created for UID:', user.uid);
  } catch (error) {
    console.error('Error creating user profile:', error);
    // Consider more robust error handling/logging
    throw error; // Re-throw or handle as needed
  }
};
